import React, {MutableRefObject, useCallback, useEffect, useState} from 'react';
import {produce} from 'immer';
import axios, {AxiosResponse} from 'axios';
import {Dropdown} from 'react-bootstrap';
import {InlineAlert} from '@/components/alerts';
import {DragPreviewImage} from 'react-dnd';
import StoryboardCellSearchButton from '@/views/storyboard/cell/StoryboardCellSearchButton';
import CellHandlerContainer from '@/views/storyboard/cellhandlers/CellHandlerContainer';
import ChartSettingsModal from '@/views/storyboard/chart_settings';
import ChartSettingsCSVDownloadButton from '@/views/storyboard/cell/ChartSettingsCSVDownloadButton';
import ChartExplainerIconButton from '@/views/storyboard/components/ChartExplainerIconButton';
import ChartExplanationDisplay from '@/views/storyboard/components/ChartExplanationDisplay';
import ChartCardDescription from '@/views/storyboard/cell/ChartCardDescription';
import {Chart, ChartPostData, ChartQuery, ChartSettings} from 'entities/chart';
import {useStoreActions, useStoreState} from 'store';
import {ConnectDragPreview} from 'react-dnd';
import DateRangePicker from 'react-bootstrap-daterangepicker/dist';
import moment, {Moment} from 'moment';
import {ChartType, deprecatedChartTypes} from '@/views/storyboard/utils/chartConstants';
import {SingleDatePickerForm} from '@/components/utils/SingleDatePickerForm';
import useOptimizedConfig from '@/hooks/useOptimizedConfig';
import i18n from 'i18n';
import {ModalAlert} from '@/components/alerts';
import Icon from '@/components/icons/Icon';
import Button from '@/components/ui/Button';
import {Tooltip} from '@/components/tooltips';


interface Props {
    id: number;
    chartQuery: ChartQuery;
    chart: ChartSettings;
    writeAccess: boolean;
    deleteChart: (index: number) => void;
    onNewChartSave: (chartData: Chart, toIndex?: number) => void;
    modifyChart: (chartData: Chart) => void;
    moveChart: (dragIndex: number, hoverIndex: number) => void;
    onChartMove: () => void;
    isDragging: boolean;
    index: number;

    innerRef: MutableRefObject<HTMLDivElement>;
    connectDragPreview?: ConnectDragPreview;

    hasError?: boolean;
}


const StoryboardCellInnerContent = React.memo<Props>((
    {
        id,
        chartQuery,
        chart,
        writeAccess,
        deleteChart,
        onNewChartSave,
        modifyChart,

        hasError = false,

        isDragging,
        connectDragPreview = () => null,
        index,

        innerRef,
    },
) => {
    const [showExplanation, setShowExplanation] = useState(false);
    const {userData, loading: loadingUserData} = useOptimizedConfig();

    const [dateRangeTrendingPeriod, setDateRangeTrendingPeriod] = useState<string>('');

    const [trendingPeriodChange, setTrendingPeriodChange] = useState<boolean>(false);
    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState<boolean>(false);
    const [isDeleting, setIsDeleting] = useState<boolean>(false);

    const setFilterSidebarOpenMem = useStoreActions(
        (actions) => actions.filterSidebarOpen.setFilterSidebarOpenWithMemory,
    );
    const activeStoryboard = useStoreState((state) => state.activeStoryboard.activeStoryboard);

    const [modalShown, setModalShown] = useState<boolean>(false);
    const [modalHasBeenOpened, setModalHasBeenOpened] = useState<boolean>(false);

    const searchQueryAllFields = useStoreState((state) => state.search.searchQueryAllFields);
    const haveGlobalFilters = searchQueryAllFields && Object.keys(searchQueryAllFields).length;
    const isDeprecatedChartType = deprecatedChartTypes.includes(chart.type);
    const deprecatedChartTypeLabel = chart.type === ChartType.Concordance
        ? i18n.t('concordance.text')
        : i18n.t('trending_words.text');


    useEffect(() => {
        if (modalShown) {
            setModalHasBeenOpened(true);
        }

        if (modalHasBeenOpened || modalShown) {
            setFilterSidebarOpenMem(!!(modalShown && haveGlobalFilters));
        }
    }, [
        modalShown,
        modalHasBeenOpened,
        haveGlobalFilters,
        setFilterSidebarOpenMem,
    ]);

    const onModalClose = useCallback(() => {
        setModalShown(false);
    }, [setModalShown]);

    const onChartSave = (chartData: Chart) => {
        try {
            modifyChart(chartData);

            if (chartData.storyboardId === activeStoryboard.id) {
                setModalShown(false);
            }
        } catch (error) {
            console.error('❌ Error in onChartSave:', error);
            throw error;
        }
    };

    const duplicateItself = async () => {
        
        try {
            const chartSettingsData = produce(chart, (draft) => {
                delete draft.id;
                draft.storyboardId = activeStoryboard.id;
            });

            const chartQueryData = produce(chartQuery, (draft) => {
                delete draft.id;
            });

            const response = await axios.post<Chart, AxiosResponse<Chart, ChartPostData>, ChartPostData>(window.URLS.chartQueries, {
                ...chartSettingsData,
                search_query: chartQueryData,
            });

            onNewChartSave(
                response.data,
                index,
            );
        } catch (error) {
            console.error('❌ Error in duplicateItself:', error);
            throw error;
        }
    };

    const deleteItself = () => {
        setDeleteConfirmOpen(true);
    };

    const closeDeleteConfirm = (): void => {
        setDeleteConfirmOpen(false);
        setIsDeleting(false);
    };

    const confirmDelete = async (): Promise<void> => {
        setIsDeleting(true);
        try {
            await axios.delete(`${window.URLS.chartQueries}/${chart.id}`);
            deleteChart(index);
            closeDeleteConfirm();
        } catch (error) {
            setIsDeleting(false);
            throw error;
        }
    };

    let settingsDropdown;

    if (writeAccess) {
        settingsDropdown = (
            <Tooltip
                content={i18n.t('jt_settings.text')}
                placement="top"
            >
                    <Dropdown className="d-inline-block">
                    <Dropdown.Toggle
                        as={Button}
                        variant="outline-info"
                        size="sm"
                        icon="mdi-settings"
                        className="dropdown-toggle-no-caret"
                        id={`settings-dropdown-${id}`}
                        data-cy="chart-settings-toggle"
                    />

                    <Dropdown.Menu align="end">
                        <Dropdown.Item
                            onClick={() => setModalShown(true)}
                            data-cy="chart-settings-btn"
                        >
                            <Icon name="mdi-settings" className="mr-2"/> {i18n.t('jt_settings.text')}
                        </Dropdown.Item>
                        <Dropdown.Item
                            onClick={(e) => {
                                duplicateItself();
                            }}
                            data-cy="chart-duplicate-btn"
                        >
                            <Icon name="mdi-content-copy" className="mr-2"/> {i18n.t('jt_duplicate.text')}
                        </Dropdown.Item>
                        <ChartSettingsCSVDownloadButton
                            chartId={chart.id}
                            chartType={chart.type}
                        />
                        <Dropdown.Item
                            onClick={() => deleteItself()}
                            data-cy="chart-settings-delete-btn"
                        >
                            <Icon name="mdi-delete-outline" className="mr-2"/> {i18n.t('jt_delete.text')}
                        </Dropdown.Item>
                    </Dropdown.Menu>
                </Dropdown>
            </Tooltip>
        );
    }

    const storyboardCellClasses = ['storyboard-cell'];
    if (isDragging) {
        storyboardCellClasses.push('in-drag');
    }
    if (showExplanation) {
        storyboardCellClasses.push('storyboard-cell--explanation-open');
    }

    const onPickerApply = (): void => {
        setTrendingPeriodChange(true);
        setTrendingPeriodChange(false);
    };

    const onHandleTrendingPeriodEvent = (trendingPeriod: Moment) => {
        setDateRangeTrendingPeriod(trendingPeriod.toDate().toLocaleDateString('en-GB'));
    };

    // Safely derive initial moments for the Trending Words single-date picker.
    // This avoids passing empty/invalid values to the DateRangePicker, which would
    // otherwise render "Invalid date" in the input.
    let trendingStartMoment: Moment | null = null;
    let trendingMinMoment: Moment | null = null;
    let trendingMaxMoment: Moment | null = null;

    if (!loadingUserData && userData) {
        if (userData.sessionStartDate) {
            const min = moment(userData.sessionStartDate, 'DD/MM/YYYY');
            trendingMinMoment = min.isValid() ? min : null;
        }
        if (userData.sessionEndDate) {
            const max = moment(userData.sessionEndDate, 'DD/MM/YYYY');
            trendingMaxMoment = max.isValid() ? max : null;
        }

        // If the user has already picked a custom trending period, prefer that.
        if (dateRangeTrendingPeriod) {
            const picked = moment(dateRangeTrendingPeriod, 'DD/MM/YYYY');
            if (picked.isValid()) {
                trendingStartMoment = picked;
            }
        }

        // Fallback: use session end date minus one day.
        if (!trendingStartMoment && trendingMaxMoment) {
            trendingStartMoment = trendingMaxMoment.clone().subtract(1, 'days');
        }

        // Final fallback: yesterday from today.
        if (!trendingStartMoment) {
            trendingStartMoment = moment().subtract(1, 'days');
        }
    }

    chartQuery = produce(chartQuery, (draft) => {
        draft.trending_period = dateRangeTrendingPeriod;
    });

    return (
        <div
            className={storyboardCellClasses.join(' ')}
            data-cy={`storyboard-cell-${id}`}
        >
            <DragPreviewImage
                // Empty image to disable drag preview
                src={'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mP8z'
                    + '/C/HgAGgwJ/lK3Q6wAAAABJRU5ErkJggg=='}
                connect={connectDragPreview}
            />
            <div
                ref={innerRef}
                className={[
                    'storyboard-cell-inner',
                    `charttype-${chart.type}`,
                ].join(' ')}
            >
                <div className="storyboard-cell-settings-bar">
                    <div className="storyboard-cell-settings-bar-content">
                        <h4 style={{margin: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis'}} title={chart.title}>
                            {chart.title}
                            {chart.type === ChartType.TrendingWords && trendingStartMoment ? (
                                <div id="trending-words-date-container">
                                    <DateRangePicker
                                        initialSettings={{
                                            startDate: trendingStartMoment,
                                            minDate: trendingMinMoment || undefined,
                                            maxDate: trendingMaxMoment || undefined,
                                            singleDatePicker: true,
                                            locale: {
                                                format: 'DD/MM/YYYY',
                                                firstDay: 1,
                                            },
                                            showWeekNumbers: true,
                                            opens: 'left',
                                            alwaysShowCalendars: true,
                                        }}
                                        onApply={onPickerApply}
                                        onCallback={onHandleTrendingPeriodEvent}
                                    >
                                        <SingleDatePickerForm onFormSubmit={onPickerApply}/>
                                    </DateRangePicker>
                                </div>) : null}
                        </h4>
                        {chart.description?.trim() ? (
                            <ChartCardDescription description={chart.description} />
                        ) : null}
                    </div>

                    <div className="storyboard-cell-settings-bar-actions">
                        {
                            chart.type !== 'comment' && !hasError ? (
                                <span className="mr-2">
                                    <ChartExplainerIconButton 
                                        chartId={id}
                                        chartTitle={chart.title}
                                        chartType={chart.type}
                                        onToggleExplanation={() => setShowExplanation(!showExplanation)}
                                    />
                                </span>
                            ) : null
                        }

                        {
                            chart.type !== 'comment' ? (
                                <span className="mr-2">
                                    <StoryboardCellSearchButton chartQuery={chartQuery}/>
                                </span>
                            ) : null
                        }

                        {
                            !hasError ? settingsDropdown : null
                        }
                    </div>
                </div>
                {isDeprecatedChartType ? (
                    <InlineAlert
                        variant="warning"
                        icon="mdi-information-outline"
                        message={i18n.t('chart_deprecated_warning.text', {chartType: deprecatedChartTypeLabel})}
                        className="storyboard-cell-deprecation-warning"
                    />
                ) : null}

                {
                    !hasError ? (
                        <>
                            <div className="storyboard-cell-chart-area">
                                <CellHandlerContainer
                                    chart={chart}
                                    chartQuery={chartQuery}
                                    previewOnly={false}
                                />
                            </div>

                            <ChartSettingsModal
                                key={`${chart?.id ?? ''}-${modalShown}`}
                                onSave={(chartData) => onChartSave(chartData)}
                                chartQuery={chartQuery}
                                chartSettings={chart}
                                activeStoryboardId={activeStoryboard.id}
                                onModalHide={onModalClose}
                                comment={chart.type === 'comment'}

                                modalProps={{
                                    show: modalShown,
                                }}
                            />
                        </>
                    ) : (
                        <InlineAlert 
                            variant="error" 
                            message={i18n.t('jt_something_went.text')}
                            className="mx-4"
                        />
                    )
                }
            </div>

            {chart.type !== 'comment' && !hasError ? (
                <ChartExplanationDisplay
                    chartId={id}
                    isVisible={showExplanation}
                    onToggleVisibility={() => setShowExplanation(!showExplanation)}
                />
            ) : null}

            <ModalAlert
                isOpen={deleteConfirmOpen}
                onClose={closeDeleteConfirm}
                variant="error"
                title={i18n.t('jt_confirm_deletion.text')}
                message={i18n.t('jt_delete_chart.text', {title: chart.title})}
                actions={[
                    {
                        label: i18n.t('jt_delete.text'),
                        onClick: confirmDelete,
                        variant: 'danger',
                        loading: isDeleting
                    }
                ]}
                dismissible={!isDeleting}
            />
        </div>
    );
});

export default StoryboardCellInnerContent;
